import { useState, useEffect } from "react";
import { Menu, X, ArrowRight, Phone, Mail } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/logo.png";
import { useLanguage, languageLabels, type Language } from "@/i18n/LanguageContext";
import { getBookingUrl } from "@/lib/booking";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const { language, setLanguage, t } = useLanguage();

  const navLinks = [
    { name: t.nav.home, href: "/" },
    { name: t.nav.hotel, href: "/about" },
    { name: t.nav.rooms, href: "/rooms" },
    { name: t.nav.restaurant, href: "/restaurant" },
    { name: t.nav.badRagaz, href: "/bad-ragaz" },
    { name: t.nav.contact, href: "/contact" },
  ];

  // On homepage: transparent until scrolled. On subpages: always solid.
  const isTransparent = isHome && !isScrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${
          isTransparent
            ? "bg-transparent py-4"
            : "bg-card shadow-elevated py-3"
        }`}
      >
        <div className="hotel-container">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="relative shrink-0 z-[60]">
              <div className={`transition-all duration-500 rounded-sm ${isTransparent ? "bg-white/90 p-1.5" : ""}`}>
                <img
                  src={logo}
                  alt="Hotel Quelle Bad Ragaz"
                  className={`w-auto transition-all duration-500 ${
                    isScrolled ? "h-10 md:h-12" : "h-12 md:h-14"
                  }`}
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.href;
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`relative px-3 py-2 font-body text-[11px] uppercase tracking-[0.1em] font-medium transition-all duration-300 ${
                      isTransparent
                        ? isActive
                          ? "text-primary-foreground"
                          : "text-primary-foreground/80 hover:text-primary-foreground"
                        : isActive
                        ? "text-primary"
                        : "text-foreground/70 hover:text-foreground"
                    }`}
                  >
                    {link.name}
                    {isActive && (
                      <span className={`absolute bottom-0 left-3 right-3 h-px ${isTransparent ? "bg-primary-foreground" : "bg-primary"}`} />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop Right: Book Now + Phone + Language */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href={getBookingUrl(language)}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 bg-white text-foreground font-body text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-white/90 transition-all duration-300 rounded-sm cursor-pointer border border-white/80"
              >
                {t.nav.bookNow}
              </a>
              <a
                href="tel:+41813021113"
                className="flex items-center gap-2 px-4 py-2.5 bg-white text-foreground rounded-sm font-body text-[11px] uppercase tracking-[0.15em] transition-all duration-300 border border-white/80 hover:bg-white/90"
              >
                <Phone className="w-3.5 h-3.5" />
                +41 81 302 1113
              </a>
              <select
                className="px-4 py-2.5 bg-white text-foreground rounded-sm font-body text-[11px] uppercase tracking-[0.15em] border border-white/80 cursor-pointer hover:bg-white/90 transition-all duration-300 outline-none"
                value={language}
                onChange={(e) => setLanguage(e.target.value as Language)}
              >
                {(Object.keys(languageLabels) as Language[]).map((lang) => (
                  <option key={lang} value={lang}>{languageLabels[lang]}</option>
                ))}
              </select>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden relative z-[60] w-10 h-10 flex items-center justify-center transition-colors duration-300 ${
                isTransparent && !isMobileMenuOpen
                  ? "text-primary-foreground"
                  : "text-foreground"
              }`}
              aria-label={isMobileMenuOpen ? "Menü schliessen" : "Menü öffnen"}
            >
              <span className={`absolute transition-all duration-300 ${isMobileMenuOpen ? "opacity-100 rotate-0" : "opacity-0 rotate-90"}`}>
                <X className="w-6 h-6" />
              </span>
              <span className={`absolute transition-all duration-300 ${isMobileMenuOpen ? "opacity-0 -rotate-90" : "opacity-100 rotate-0"}`}>
                <Menu className="w-6 h-6" />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[55] transition-all duration-500 lg:hidden ${
          isMobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Background */}
        <div className="absolute inset-0 bg-card" />

        {/* Content */}
        <div className="relative h-full flex flex-col pt-24 pb-8 px-8 overflow-y-auto">
          {/* Navigation Links */}
          <nav className="flex flex-col gap-1 mb-auto">
            {navLinks.map((link, i) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`group flex items-center justify-between py-4 border-b border-border/50 transition-all duration-300 ${
                    isMobileMenuOpen
                      ? "translate-x-0 opacity-100"
                      : "-translate-x-8 opacity-0"
                  }`}
                  style={{ transitionDelay: isMobileMenuOpen ? `${150 + i * 60}ms` : "0ms" }}
                >
                  <span
                    className={`font-display text-2xl font-light tracking-wide transition-colors duration-300 ${
                      isActive
                        ? "text-accent"
                        : "text-foreground group-hover:text-accent"
                    }`}
                  >
                    {link.name}
                  </span>
                  <ArrowRight
                    className={`w-4 h-4 transition-all duration-300 ${
                      isActive
                        ? "text-accent opacity-100"
                        : "text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:text-accent"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          {/* CTA Button */}
          <div
            className={`mt-8 transition-all duration-500 ${
              isMobileMenuOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            }`}
            style={{ transitionDelay: isMobileMenuOpen ? "550ms" : "0ms" }}
          >
            <a
              href={getBookingUrl(language)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-center px-8 py-4 bg-primary text-primary-foreground font-body text-xs uppercase tracking-[0.25em] font-semibold rounded-sm cursor-pointer hover:bg-hotel-bordeaux-light transition-colors duration-300"
            >
              {t.nav.bookNow}
            </a>
          </div>

          {/* Contact Info */}
          <div
            className={`mt-6 pt-6 border-t border-border/50 flex flex-col gap-3 transition-all duration-500 ${
              isMobileMenuOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            }`}
            style={{ transitionDelay: isMobileMenuOpen ? "650ms" : "0ms" }}
          >
            <a
              href="tel:+41813021113"
              className="flex items-center gap-3 text-muted-foreground hover:text-accent transition-colors"
            >
              <Phone className="w-4 h-4 text-accent" />
              <span className="font-body text-sm">+41 81 302 11 13</span>
            </a>
            <a
              href="mailto:info@esoshotelquelle.com"
              className="flex items-center gap-3 text-muted-foreground hover:text-accent transition-colors"
            >
              <Mail className="w-4 h-4 text-accent" />
              <span className="font-body text-sm">info@esoshotelquelle.com</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
